import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Building, User, ShieldCheck, Search, Brain, ArrowRight, MailOpen, Clock, Inbox, ArrowLeft } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import useToast from '../../hooks/useToast';

// ─── Feature cards data ───
const FEATURES = [
  {
    icon: Search,
    title: 'Smart Lead Discovery',
    description: 'Search any industry and get verified businesses with full contact data in seconds.',
  },
  {
    icon: Brain,
    title: 'AI-Powered Scoring',
    description: 'Priority rankings and ready-to-use pitch angles so you know exactly who to contact first.',
  },
];

// ─── Testimonial ───
const TESTIMONIAL = {
  quote: 'The fastest path from search to closed deal we\'ve ever used.',
  author: 'David Chen',
  role: 'VP Sales, Pipeline360',
};

// ─── Password strength calculator ───
function getPasswordStrength(password) {
  let score = 0;
  if (!password) return { score: 0, label: '', color: '', segments: [0, 0, 0, 0] };
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  const segments = [0, 0, 0, 0];
  const filledSegments = Math.min(Math.ceil(score / 5 * 4), 4);
  for (let i = 0; i < filledSegments; i++) segments[i] = 1;

  if (score <= 1) return { score, label: 'Weak', color: '#EF4444', segments };
  if (score <= 2) return { score, label: 'Fair', color: '#F59E0B', segments };
  if (score <= 3) return { score, label: 'Good', color: '#F59E0B', segments };
  return { score, label: 'Strong', color: '#10B981', segments };
}

// ─── P3: Canvas + WebGL Fingerprinting ─────────────────────────
function getCanvasFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200; canvas.height = 50;
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(0, 0, 100, 50);
    ctx.fillStyle = '#069';
    ctx.fillText('iLeadX Registration', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Device Check', 4, 32);
    const data = canvas.toDataURL();
    // Hash the pixel data for storage
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      hash = ((hash << 5) - hash) + data.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash).toString(16);
  } catch { return ''; }
}

function getWebGLRenderer() {
  try {
    const gl = document.createElement('canvas').getContext('webgl')
            || document.createElement('canvas').getContext('experimental-webgl');
    if (!gl) return '';
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return '';
    return gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '';
  } catch { return ''; }
}
// ───────────────────────────────────────────────────────────────

// ─── P3: Registration persistence marker (Cookies + LocalStorage) ──
const REG_STORAGE_KEY = 'ileadx_reg_attempt';
const REG_COOKIE = 'ileadx_reg_attempt';

function setRegistrationMarker() {
  try { localStorage.setItem(REG_STORAGE_KEY, Date.now().toString()); } catch {}
  try {
    const d = new Date();
    d.setTime(d.getTime() + 24 * 60 * 60 * 1000);
    document.cookie = `${REG_COOKIE}=1;expires=${d.toUTCString()};path=/;SameSite=Lax`;
  } catch {}
}

function hasRecentRegistration() {
  try {
    const stored = localStorage.getItem(REG_STORAGE_KEY);
    if (stored && Date.now() - parseInt(stored, 10) < 24 * 60 * 60 * 1000) return true;
  } catch {}
  try {
    if (document.cookie.split(';').some(c => c.trim().startsWith(`${REG_COOKIE}=`))) return true;
  } catch {}
  return false;
}
// ───────────────────────────────────────────────────────────────

export default function Register() {
  const getLandingUrl = (path) => {
    const base = window.location.hostname === 'localhost' ? 'http://localhost:8000' : 'https://ileadx.io';
    return base + path;
  };
  const inviteToken = new URLSearchParams(window.location.search).get('invite_token');
  const [form, setForm] = useState({ full_name: '', company_name: '', email: '', password: '', invite_token: inviteToken || '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [error, setError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const pageLoadTime = React.useRef(Date.now());
  // Mobile: form only (value panel hidden on mobile)
  const navigate = useNavigate();
  const toastHook = useToast();

  const passwordStrength = getPasswordStrength(form.password);

  const handleGoogleSignup = async () => {
    if (!termsAccepted) {
      toast('Terms & Privacy', { description: 'Please accept the Terms of Service and Privacy Policy to continue.', duration: 5000 });
      return;
    }
    try {
      const res = await api.get('/api/auth/google', { params: { redirect: window.location.origin + '/onboarding' } });
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (err) {
      toast('Google sign-up failed.', { description: 'Please try again or use email to create an account.', duration: 5000 });
    }
  };

  const handleLinkedInSignup = async () => {
    if (!termsAccepted) {
      toast('Terms Required', { description: 'Please accept the Terms of Service and Privacy Policy before signing up.', duration: 5000 });
      return;
    }
    try {
      const res = await api.get('/api/auth/linkedin', { params: { redirect: window.location.origin + '/onboarding' } });
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (err) {
      toast('LinkedIn sign-up failed.', { description: 'Please try again or use email to create an account.', duration: 5000 });
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    // Browser session integrity: check for recent registration from same browser
    if (hasRecentRegistration()) {
      setError('Registration already attempted from this browser. Please wait 24 hours or use a different device.');
      toast.error('Registration already attempted from this browser. Please wait 24 hours.');
      return;
    }
    if (!form.full_name.trim()) {
      setError('Please enter your full name');
      toast('Full name is required.', { description: 'Please enter your full name to continue.', duration: 4000 });
      return;
    }
    if (!form.email.trim()) {
      setError('Please enter your email address');
      toast('Email is required.', { description: 'Please enter your email address.', duration: 4000 });
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      toast('Password too short.', { description: 'Password must be at least 8 characters.', duration: 4000 });
      return;
    }
    setLoading(true);
    setError('');
    try {
      const botHeaders = {
        'x-submission-time': String(((Date.now() - pageLoadTime.current) / 1000).toFixed(1)),
        'x-automated-browser': navigator.webdriver ? 'true' : 'false',
        'x-canvas-fingerprint': getCanvasFingerprint(),
        'x-webgl-renderer': getWebGLRenderer(),
      };
      await api.post('/api/auth/register', form, { headers: botHeaders });
      setRegistered(true);
      setRegistrationMarker();
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // ─── Shared styles ───
  const labelStyle = {
    display: 'block',
    color: 'var(--text-secondary)',
    fontSize: 'var(--text-sm)',
    fontWeight: 500,
    marginBottom: 6,
    textAlign: 'left',
  };

  const inputFieldStyle = {
    width: '100%',
    padding: '12px 14px',
    background: 'var(--input-bg)',
    border: '1px solid var(--input-border)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--text-primary)',
    fontSize: 'var(--text-base)',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  };

  const focusStyles = (e) => {
    e.target.style.borderColor = 'var(--accent-primary)';
    e.target.style.boxShadow = '0 0 0 3px var(--accent-glow)';
  };
  const blurStyles = (e) => {
    e.target.style.borderColor = 'var(--input-border)';
    e.target.style.boxShadow = 'none';
  };

  // ================================================================
  // POST-REGISTRATION: CHECK YOUR EMAIL PAGE
  // ================================================================
  if (registered) {
    const successValuePanel = (
      <div style={{
        background: 'linear-gradient(160deg, #0A1628 0%, #0F1D3A 40%, #132244 100%)',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 'clamp(32px, 6vw, 48px)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.03,
          backgroundImage: `radial-gradient(circle at 30% 20%, #10B981 1px, transparent 1px),
                            radial-gradient(circle at 70% 80%, #2563EB 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }} />
        <div style={{
          position: 'absolute',
          width: 'min(350px, 70vw)', height: 'min(350px, 70vw)',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(16, 185, 129, 0.06) 0%, transparent 70%)',
          top: '15%', right: '-15%',
        }} />

        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 380, width: '100%' }}>
          {/* Large mail icon */}
          <div style={{
            width: 80, height: 80,
            borderRadius: '50%',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '2px solid rgba(16, 185, 129, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto var(--space-2xl)',
          }}>
            <MailOpen size={36} color="#10B981" />
          </div>

          {/* Testimonial */}
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-xl)',
            marginBottom: 'var(--space-2xl)',
          }}>
            <p style={{
              color: '#E2E8F0',
              fontSize: 'var(--text-base)',
              lineHeight: 1.7,
              fontStyle: 'italic',
              margin: '0 0 var(--space-md)',
            }}>
              "First lead in under 2 minutes. The AI scoring is scary accurate."
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', justifyContent: 'center' }}>
              <div style={{
                width: 32, height: 32,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #2563EB, #3B82F6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontSize: 'var(--text-xs)', fontWeight: 600,
              }}>
                A
              </div>
              <div>
                <p style={{ color: '#F1F5F9', fontSize: 'var(--text-sm)', fontWeight: 600, margin: 0 }}>
                  Alex T.
                </p>
                <p style={{ color: '#94A3B8', fontSize: 'var(--text-xs)', margin: '2px 0 0' }}>
                  Founder, Pipeline AI
                </p>
              </div>
            </div>
          </div>

          {/* Trust badges */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-md)',
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px',
              background: 'rgba(37, 99, 235, 0.08)',
              border: '1px solid rgba(37, 99, 235, 0.15)',
              borderRadius: 'var(--radius-full)',
              justifyContent: 'center',
              margin: '0 auto',
            }}>
              <span style={{ fontSize: 14 }}>🚀</span>
              <span style={{ color: '#93C5FD', fontSize: 'var(--text-xs)', fontWeight: 500 }}>
                2,000+ teams onboarded
              </span>
            </div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px',
              background: 'rgba(16, 185, 129, 0.08)',
              border: '1px solid rgba(16, 185, 129, 0.15)',
              borderRadius: 'var(--radius-full)',
              justifyContent: 'center',
              margin: '0 auto',
            }}>
              <ShieldCheck size={14} color="#10B981" />
              <span style={{ color: '#6EE7B7', fontSize: 'var(--text-xs)', fontWeight: 500 }}>
                No spam ever
              </span>
            </div>
          </div>
        </div>
      </div>
    );

    const successFormPanel = (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(24px, 6vw, 48px)',
        background: 'var(--bg-primary)',
        minHeight: '100%',
      }}>
        <div style={{
          width: '100%',
          maxWidth: 440,
        }}>
          {/* Brand */}
          <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }}>
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <img src="/iLeadX-logo.png" alt="iLeadX" style={{ height: 40, width: 'auto' }} />
            </div>
            <h1 style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 700,
              color: 'var(--text-primary)',
              margin: '0 0 8px',
              letterSpacing: '-0.5px',
            }}>
              Check Your Email
            </h1>
            <p style={{
              color: 'var(--text-secondary)',
              fontSize: 'var(--text-sm)',
              margin: 0,
            }}>
              We sent a verification link to
            </p>
          </div>

          {/* Email display card */}
          <div style={{
            background: 'var(--bg-tertiary)',
            border: '1px solid var(--border-primary)',
            borderRadius: 'var(--radius-md)',
            padding: '16px',
            marginBottom: 'var(--space-xl)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            <div style={{
              width: 40, height: 40,
              borderRadius: 'var(--radius-md)',
              background: 'var(--accent-glow)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Mail size={20} color="var(--accent-primary)" />
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{
                color: 'var(--text-primary)',
                fontSize: 'var(--text-base)',
                fontWeight: 500,
                margin: 0,
                wordBreak: 'break-all',
              }}>
                {form.email}
              </p>
              <p style={{
                color: 'var(--text-tertiary)',
                fontSize: 'var(--text-xs)',
                margin: '2px 0 0',
              }}>
                Verification email sent
              </p>
            </div>
          </div>

          {/* Instructions */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-md)',
            marginBottom: 'var(--space-2xl)',
          }}>
            <div style={{
              display: 'flex',
              gap: 'var(--space-md)',
              padding: 'var(--space-md) var(--space-lg)',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-primary)',
              borderRadius: 'var(--radius-md)',
              alignItems: 'flex-start',
            }}>
              <div style={{
                width: 32, height: 32,
                borderRadius: 'var(--radius-md)',
                background: 'var(--accent-glow)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Inbox size={16} color="var(--accent-primary)" />
              </div>
              <div>
                <p style={{
                  color: 'var(--text-primary)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 600,
                  margin: '0 0 2px',
                }}>
                  Click the link in your inbox
                </p>
                <p style={{
                  color: 'var(--text-tertiary)',
                  fontSize: 'var(--text-xs)',
                  margin: 0,
                  lineHeight: 1.4,
                }}>
                  Open the email from iLeadX and click "Verify My Email" to activate your account.
                </p>
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: 'var(--space-md)',
              padding: 'var(--space-md) var(--space-lg)',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-primary)',
              borderRadius: 'var(--radius-md)',
              alignItems: 'flex-start',
            }}>
              <div style={{
                width: 32, height: 32,
                borderRadius: 'var(--radius-md)',
                background: 'var(--warning-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Clock size={16} color="var(--warning)" />
              </div>
              <div>
                <p style={{
                  color: 'var(--text-primary)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 600,
                  margin: '0 0 2px',
                }}>
                  Check spam if you don't see it
                </p>
                <p style={{
                  color: 'var(--text-tertiary)',
                  fontSize: 'var(--text-xs)',
                  margin: 0,
                  lineHeight: 1.4,
                }}>
                  The email should arrive within 2 minutes. If not, check your spam or promotions folder.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <button
            onClick={() => navigate('/login')}
            className="auth-button auth-button-primary"
            style={{
              height: 50,
              fontSize: 'var(--text-base)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              width: '100%',
            }}
          >
            Go to Login <ArrowRight size={18} />
          </button>
        </div>
      </div>
    );

    return (
      <>
        {/* Desktop: split screen */}
        <div className="auth-container" style={{ display: 'none' }} data-desktop>
          <div style={{ width: '40%', minHeight: '100%' }}>{successValuePanel}</div>
          <div style={{ width: '60%', minHeight: '100%' }}>{successFormPanel}</div>
        </div>

        {/* Mobile: single column */}
        <div className="auth-container" style={{ flexDirection: 'column' }} data-mobile>
          <style>{`
            @media (min-width: 769px) {
              [data-mobile] { display: none !important; }
              [data-desktop] { display: flex !important; }
            }
            @media (max-width: 768px) {
              [data-mobile] { display: flex !important; }
              [data-desktop] { display: none !important; }
            }
          `}</style>

          {/* Compact testimonial banner */}
          <div style={{
            background: 'linear-gradient(135deg, #0A1628, #0F1D3A)',
            padding: '14px 20px',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <div style={{
              width: 28, height: 28,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #2563EB, #3B82F6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: 'var(--text-xs)', fontWeight: 600,
              flexShrink: 0,
            }}>
              A
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{
                color: '#E2E8F0',
                fontSize: 'var(--text-xs)',
                fontStyle: 'italic',
                margin: 0,
                lineHeight: 1.4,
              }}>
                "First lead in under 2 minutes."
              </p>
              <p style={{ color: '#94A3B8', fontSize: 'var(--text-xs)', margin: '2px 0 0' }}>
                Alex T., Founder, Pipeline AI
              </p>
            </div>
          </div>

          <div style={{ flex: 1, overflow: 'auto' }}>
            {successFormPanel}
          </div>
        </div>
      </>
    );
  }

  // ================================================================
  // REGISTRATION FORM (unchanged)
  // ================================================================
  const valuePanelContent = (
    <div style={{
      background: 'linear-gradient(160deg, #0A1628 0%, #0F1D3A 40%, #132244 100%)',
      minHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: 'clamp(32px, 6vw, 48px)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.03,
        backgroundImage: `radial-gradient(circle at 30% 20%, #2563EB 1px, transparent 1px),
                          radial-gradient(circle at 70% 80%, #F59E0B 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
      }} />
      <div style={{
        position: 'absolute',
        width: 'min(350px, 70vw)', height: 'min(350px, 70vw)',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(37, 99, 235, 0.06) 0%, transparent 70%)',
        bottom: '5%', left: '-15%',
        animation: 'glowPulse 4s ease-in-out infinite',
      }} />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 420, width: '100%', margin: '0 auto' }}>
        <h2 style={{
          color: '#F1F5F9',
          fontSize: 'clamp(1.25rem, 3.5vw, 1.625rem)',
          fontWeight: 700,
          margin: '0 0 var(--space-xl)',
          lineHeight: 1.25,
          letterSpacing: '-0.3px',
        }}>
          Everything you need to find and close B2B leads
        </h2>

        <div style={{
          textAlign: 'center',
          padding: 'var(--space-xl) var(--space-lg)',
          background: 'rgba(37, 99, 235, 0.1)',
          border: '1px solid rgba(37, 99, 235, 0.2)',
          borderRadius: 'var(--radius-lg)',
          marginBottom: 'var(--space-xl)',
          animation: 'floatUp 0.4s ease both',
        }}>
          <div style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'clamp(2rem, 6vw, 3rem)',
            fontWeight: 700,
            color: '#60A5FA',
            lineHeight: 1,
            marginBottom: 6,
          }}>
            10x
          </div>
          <p style={{ color: '#94A3B8', fontSize: 'var(--text-sm)', margin: 0, fontWeight: 500 }}>
            average ROI in the first 60 days
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', marginBottom: 'var(--space-xl)' }}>
          {FEATURES.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div key={i} style={{
                display: 'flex', gap: 'var(--space-md)',
                padding: 'var(--space-lg)',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.05)',
                borderLeft: '3px solid #3B82F6',
                borderRadius: 'var(--radius-md)',
                animation: `floatUp 0.4s ease ${0.15 + i * 0.1}s both`,
              }}>
                <div style={{
                  width: 40, height: 40,
                  borderRadius: 'var(--radius-md)',
                  background: 'rgba(37, 99, 235, 0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Icon size={20} color="#60A5FA" />
                </div>
                <div>
                  <h3 style={{ color: '#F1F5F9', fontSize: 'var(--text-sm)', fontWeight: 600, margin: '0 0 4px' }}>
                    {feature.title}
                  </h3>
                  <p style={{ color: '#94A3B8', fontSize: 'var(--text-xs)', margin: 0, lineHeight: 1.5 }}>
                    {feature.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          padding: 'var(--space-md)',
          background: 'rgba(16, 185, 129, 0.08)',
          border: '1px solid rgba(16, 185, 129, 0.15)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-lg)',
        }}>
          <ShieldCheck size={18} color="#10B981" />
          <span style={{ color: '#6EE7B7', fontSize: 'var(--text-sm)', fontWeight: 500 }}>
            No credit card required · Cancel anytime
          </span>
        </div>

        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-lg)',
          marginBottom: 'var(--space-lg)',
        }}>
          <p style={{ color: '#E2E8F0', fontSize: 'var(--text-sm)', lineHeight: 1.6, fontStyle: 'italic', margin: '0 0 var(--space-md)' }}>
            "{TESTIMONIAL.quote}"
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'linear-gradient(135deg, #F59E0B, #D97706)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#0F172A', fontSize: 'var(--text-xs)', fontWeight: 600,
            }}>
              {TESTIMONIAL.author.charAt(0)}
            </div>
            <div>
              <p style={{ color: '#F1F5F9', fontSize: 'var(--text-xs)', fontWeight: 600, margin: 0 }}>
                {TESTIMONIAL.author}
              </p>
              <p style={{ color: '#94A3B8', fontSize: 'var(--text-xs)', margin: '2px 0 0' }}>
                {TESTIMONIAL.role}
              </p>
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 'var(--space-md)', justifyContent: 'center',
          color: '#64748B', fontSize: 'var(--text-xs)', opacity: 0.6,
        }}>
          <span>SOC 2 Compliant</span>
          <span style={{ opacity: 0.3 }}>·</span>
          <span>256-bit Encryption</span>
          <span style={{ opacity: 0.3 }}>·</span>
          <span>GDPR Ready</span>
        </div>
      </div>
    </div>
  );

  const formContent = (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'clamp(24px, 6vw, 48px)',
      background: 'var(--bg-primary)',
      minHeight: '100%',
    }}>
      <div style={{ width: '100%', maxWidth: 440, animation: 'slideInLeft 0.5s ease' }}>
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-lg)' }}>
          {(() => {
              const landingUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
                ? 'http://localhost:8000'
                : 'https://ileadx.io';
              return (
                <a href={landingUrl} style={{ display: 'block', textAlign: 'center', marginBottom: 8 }}>
                  <img src="/iLeadX-logo.png" alt="iLeadX" style={{ height: 48, width: 'auto' }} />
                </a>
              );
            })()}
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', margin: 'var(--space-xs) 0 0' }}>
            Start your free trial
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 'var(--space-xl)' }}>
          <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--accent-primary)', color: 'white', fontSize: 'var(--text-xs)', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>1</div>
          <div style={{ width: 24, height: 1, background: 'var(--border-primary)' }} />
          <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--bg-tertiary)', color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border-primary)' }}>2</div>
          <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', marginLeft: 4 }}>Create your account</span>
        </div>

        {error && <p className="error-message">{error}</p>}

        <form onSubmit={handleRegister}>
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle} htmlFor="reg-name">Full name</label>
            <div style={{ position: 'relative' }}>
              <User size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none' }} />
              <input id="reg-name" type="text" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Sarah Johnson" style={{ ...inputFieldStyle, paddingLeft: 42 }} required autoComplete="name" onFocus={focusStyles} onBlur={blurStyles} />
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle} htmlFor="reg-company">Company name <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(optional)</span></label>
            <div style={{ position: 'relative' }}>
              <Building size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none' }} />
              <input id="reg-company" type="text" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Acme Inc." style={{ ...inputFieldStyle, paddingLeft: 42 }} autoComplete="organization" onFocus={focusStyles} onBlur={blurStyles} />
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle} htmlFor="reg-email">Email address</label>
            <div style={{ position: 'relative' }}>
              <Mail size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none' }} />
              <input id="reg-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="sarah@company.com" style={{ ...inputFieldStyle, paddingLeft: 42 }} required autoComplete="email" onFocus={focusStyles} onBlur={blurStyles} />
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle} htmlFor="reg-password">Password</label>
            <div style={{ position: 'relative' }}>
              <Lock size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none' }} />
              <input id="reg-password" type={showPassword ? 'text' : 'password'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 8 characters" style={{ ...inputFieldStyle, paddingLeft: 42, paddingRight: 42 }} required autoComplete="new-password" minLength={8} onFocus={focusStyles} onBlur={blurStyles} />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 4, display: 'flex' }} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {form.password.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                  {passwordStrength.segments.map((filled, i) => (
                    <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: filled ? passwordStrength.color : 'var(--border-primary)', transition: 'background 0.3s ease' }} />
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: passwordStrength.color, fontSize: 'var(--text-xs)', fontWeight: 500 }}>{passwordStrength.label}</span>
                  {passwordStrength.score < 4 && <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>Use 8+ characters with letters & numbers</span>}
                </div>
              </div>
            )}
          </div>

          <div style={{ marginBottom: 24, marginTop: 8 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', cursor: 'pointer', lineHeight: 1.4 }}>
              <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} style={{ accentColor: 'var(--accent-primary)', width: 16, height: 16, marginTop: 2, flexShrink: 0 }} required />
              <span>I agree to the <a href={getLandingUrl("/terms")} style={{ color: 'var(--text-link)', textDecoration: 'none' }}>Terms of Service</a> and <a href={getLandingUrl("/privacy")} style={{ color: 'var(--text-link)', textDecoration: 'none' }}>Privacy Policy</a></span>
            </label>
          </div>

          <button type="submit" disabled={loading} className="auth-button auth-button-primary" style={{ height: 50, fontSize: 'var(--text-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-sm)' }}>
            {loading ? (<><span className="spinner" /> Creating your account...</>) : (<><ArrowRight size={18} /> Start Free Trial</>)}
          </button>
        </form>

        <div className="auth-divider" style={{ margin: '24px 0' }}>or</div>

        <button
          onClick={handleGoogleSignup}
          style={{
            width: '100%', height: 48, borderRadius: 10,
            border: '1px solid var(--border-primary)', background: 'var(--bg-secondary)',
            color: 'var(--text-primary)', cursor: 'pointer', fontSize: 14, fontWeight: 500,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            transition: 'all 0.15s ease', fontFamily: 'inherit',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-tertiary)'; e.currentTarget.style.borderColor = 'var(--accent-primary)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-secondary)'; e.currentTarget.style.borderColor = 'var(--border-primary)'; }}
        >
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign up with Google
        </button>

        {/* LinkedIn Sign Up */}
        <button
          onClick={handleLinkedInSignup}
          style={{
            width: '100%', height: 48, borderRadius: 10,
            border: '1px solid var(--border-primary)', background: 'var(--bg-secondary)',
            color: 'var(--text-primary)', cursor: 'pointer',
            fontSize: 14, fontWeight: 500, marginTop: 12,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            transition: 'all 0.15s ease', fontFamily: 'inherit',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-tertiary)'; e.currentTarget.style.borderColor = '#0A66C2'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-secondary)'; e.currentTarget.style.borderColor = 'var(--border-primary)'; }}
        >
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path fill="#0A66C2" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
          Sign up with LinkedIn
        </button>

        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 24, textAlign: 'center' }}>
          Already have an account? <Link to="/login" style={{ color: 'var(--accent-primary)', fontWeight: 600, textDecoration: 'none' }}>Sign in</Link>
        </p>
      </div>
    </div>
  );

  return (
    <>
      <div className="auth-container" style={{ display: 'none' }} data-desktop>
        <div style={{ width: '55%', minHeight: '100%' }}>{formContent}</div>
        <div style={{ width: '45%', minHeight: '100%' }}>{valuePanelContent}</div>
      </div>
      <div className="auth-container" style={{ flexDirection: 'column' }} data-mobile>
        <style>{`
          @media (min-width: 769px) { [data-mobile] { display: none !important; } [data-desktop] { display: flex !important; } }
          @media (max-width: 768px) { [data-mobile] { display: flex !important; } [data-desktop] { display: none !important; } }
        `}</style>
        <div style={{ flex: 1, overflow: 'auto' }}>
            {formContent}
          </div>
      </div>
    </>
  );
}
